import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaApple, FaExclamationTriangle, FaMobileAlt, FaSimCard } from "react-icons/fa";
import "./WatchEsim.css";

type DeviceType = "apple" | "samsung";

type SetupStep = {
  title: string;
  description: string;
  image: string;
  imageAlt: string;
};

const appleSteps: SetupStep[] = [
  {
    title: "Open the Watch app",
    description: "On your iPhone, open the Apple Watch app and select the watch you want to activate.",
    image: "/assets/esim-guide/steps/apple-step-01.png",
    imageAlt: "Apple Watch app icon on an iPhone",
  },
  {
    title: "Go to Mobile or Cellular",
    description: "From the Watch app, open Mobile or Cellular to manage the watch mobile plan.",
    image: "/assets/esim-guide/steps/apple-step-02.png",
    imageAlt: "Mobile option selected in the Apple Watch app",
  },
  {
    title: "Select Set Up Mobile Plan",
    description: "Tap Set Up Mobile Plan to begin adding the companion eSIM to your watch.",
    image: "/assets/esim-guide/steps/apple-step-03.png",
    imageAlt: "Set Up Mobile option highlighted",
  },
  {
    title: "Start the setup",
    description: "Tap Set Up Mobile Plan at the bottom of the screen and wait for the activation web flow to open.",
    image: "/assets/esim-guide/steps/apple-step-04.png",
    imageAlt: "Set Up Mobile Plan button highlighted",
  },
  {
    title: "Review the connection screen",
    description: "Read the information shown, confirm you understand the charges and terms, then tap Connect.",
    image: "/assets/esim-guide/steps/apple-step-05.png",
    imageAlt: "Connect button highlighted on the companion device screen",
  },
  {
    title: "Enter your mobile service number",
    description: "Enter your mobile service number, then tap Continue. The PIN field is optional. Only enter a PIN if Jortel has specifically provided one.",
    image: "/assets/esim-guide/steps/apple-step-06.png",
    imageAlt: "Mobile service number and PIN fields highlighted",
  },
  {
    title: "Verify the SMS code",
    description: "Enter the one-time code sent by SMS to your mobile number, then tap Verify.",
    image: "/assets/esim-guide/steps/apple-step-07.png",
    imageAlt: "One-time password verification screen",
  },
  {
    title: "Wait for activation",
    description: "Leave the screen open while the activation request is processed.",
    image: "/assets/esim-guide/steps/apple-step-08.png",
    imageAlt: "Please wait screen during Apple Watch activation",
  },
  {
    title: "Accept and add device",
    description: "Review the subscription details, then tap Accept & add device to attach the watch to the companion plan.",
    image: "/assets/esim-guide/steps/apple-step-09.png",
    imageAlt: "Accept and add device button highlighted",
  },
  {
    title: "Finish setup",
    description: "When the subscription confirmation appears, tap Done. Your watch may take a short time to show the active service.",
    image: "/assets/esim-guide/steps/apple-step-10.png",
    imageAlt: "Apple Watch eSIM subscription confirmed screen",
  },
];

const samsungSteps: SetupStep[] = [
  {
    title: "Open Galaxy Wearable",
    description: "On your Samsung phone, open the Galaxy Wearable app and select the watch you want to activate.",
    image: "/assets/esim-guide/steps/samsung-step-01.png",
    imageAlt: "Galaxy Wearable app icon on a Samsung phone",
  },
  {
    title: "Go to Watch settings",
    description: "Open Watch settings from the Galaxy Wearable app.",
    image: "/assets/esim-guide/steps/samsung-step-02.png",
    imageAlt: "Watch settings option highlighted in Galaxy Wearable",
  },
  {
    title: "Open Mobile plans",
    description: "In Watch settings, select Mobile plans to add the watch companion service.",
    image: "/assets/esim-guide/steps/samsung-step-03.png",
    imageAlt: "Mobile plans option highlighted in watch settings",
  },
  {
    title: "Continue the mobile plan setup",
    description: "Review the setup information and tap Next.",
    image: "/assets/esim-guide/steps/samsung-step-04.png",
    imageAlt: "Next button highlighted on the Samsung mobile plan setup screen",
  },
  {
    title: "Review the connection screen",
    description: "Read the information shown, confirm you understand the charges and terms, then tap Connect.",
    image: "/assets/esim-guide/steps/samsung-step-05.png",
    imageAlt: "Connect button highlighted on the Samsung companion device screen",
  },
  {
    title: "Enter your mobile service number",
    description: "Enter your mobile service number, then tap Continue. The PIN field is optional. Only enter a PIN if Jortel has specifically provided one.",
    image: "/assets/esim-guide/steps/samsung-step-06.png",
    imageAlt: "Mobile service number and PIN fields highlighted on Samsung setup",
  },
  {
    title: "Verify the SMS code",
    description: "Enter the SMS verification code sent to your mobile number, then tap Verify.",
    image: "/assets/esim-guide/steps/samsung-step-07.png",
    imageAlt: "SMS verification screen for Samsung watch setup",
  },
  {
    title: "Wait for activation",
    description: "Leave the screen open while the activation request is processed.",
    image: "/assets/esim-guide/steps/samsung-step-08.png",
    imageAlt: "Please wait screen during Samsung watch activation",
  },
  {
    title: "Accept and add device",
    description: "Review the subscription details, then tap Accept & add device to attach the watch to the companion plan.",
    image: "/assets/esim-guide/steps/samsung-step-09.png",
    imageAlt: "Accept and add device button highlighted for Samsung setup",
  },
  {
    title: "Finish setup",
    description: "When the subscription confirmation appears, tap Done. Your watch may take a short time to show the active service.",
    image: "/assets/esim-guide/steps/samsung-step-10.png",
    imageAlt: "Samsung watch eSIM subscription confirmed screen",
  },
];

const removalSteps: SetupStep[] = [
  {
    title: "Use Manage Telstra Account",
    description: "Open the mobile service settings for the watch and choose Manage Telstra Account. Do not use Remove Telstra Plan if you are trying to cancel the paid service, because that only removes the eSIM profile from the watch.",
    image: "/assets/esim-guide/steps/remove-step-01.png",
    imageAlt: "Manage Telstra Account option and Remove Telstra Plan warning",
  },
  {
    title: "Remove the connected watch",
    description: "Select the connected watch, tap Remove, review the cancellation details, then choose Accept & Remove Device. Contact Jortel if you want our team to confirm the billing cancellation.",
    image: "/assets/esim-guide/steps/remove-step-02.png",
    imageAlt: "Remove connected watch and accept removal screens",
  },
];

const faqs = [
  {
    question: "What is a watch companion eSIM?",
    answer: "It is an add-on mobile service linked to an eligible primary mobile service, allowing a compatible smartwatch to use mobile connectivity when it is away from the phone.",
  },
  {
    question: "How much does it cost?",
    answer: "The eSIM Companion Plan is $10 per month inc. GST and is billed separately to your mobile plan.",
  },
  {
    question: "What devices are compatible?",
    answer: "The watch eSIM companion plan is only compatible with Apple Watch GPS + Cellular models and Samsung Galaxy Watch cellular models listed for this service. It is not compatible with Google watches, tablets, or other wearables.",
  },
  {
    question: "Can I use an Apple Watch with a Samsung phone?",
    answer: "No. The phone and watch need to stay in the same ecosystem. Use Apple Watch with iPhone, or Samsung Galaxy Watch with a compatible Samsung phone.",
  },
  {
    question: "Does my phone plan need to be eligible?",
    answer: "Yes. The companion eSIM requires an eligible 5G mobile plan with voice capability. Data-only plans are not eligible for smartwatch pairing.",
  },
  {
    question: "How much data is included?",
    answer: "The companion plan includes 1GB of data per billing cycle. If the included data is used, data access pauses until the next billing cycle. No excess data charges apply.",
  },
  {
    question: "What happens if my watch uses all its data?",
    answer: "No excess data charges apply. If the 1GB monthly allowance is used, data access pauses until the next billing cycle. Smartwatches typically use around 250MB per month, depending on how often mobile data is used away from your phone.",
  },
  {
    question: "Can I use it overseas?",
    answer: "No. The eSIM Companion Plan is domestic only and available for use in Australia.",
  },
  {
    question: "Is there a contract?",
    answer: "No. There is no lock-in contract for the companion eSIM plan.",
  },
  {
    question: "Will removing the plan from my watch cancel the service?",
    answer: "No. Removing the Telstra plan/eSIM from the watch only removes it from the device. It does not unsubscribe or cancel the paid companion plan. Contact Jortel to cancel the companion service.",
  },
  {
    question: "What happens on the first invoice?",
    answer: "The first invoice may include a pro-rated charge for the partial billing period and the next month in advance.",
  },
  {
    question: "When is the billing cycle?",
    answer: "The companion plan billing cycle runs from the 28th of each month to the 27th of the following month.",
  },
  {
    question: "Do I need the latest software?",
    answer: "Yes. Update both the phone and watch before activation to reduce setup issues.",
  },
];

export default function WatchEsim(): JSX.Element {
  const [selectedDevice, setSelectedDevice] = useState<DeviceType>("apple");
  const activeSteps = selectedDevice === "apple" ? appleSteps : samsungSteps;

  return (
    <main className="watch-esim-page">
      <section className="watch-esim-hero">
        <span>Watch eSIM companion plan</span>
        <h1>Activate mobile connectivity on your Apple or Samsung watch</h1>
        <p>
          Add a companion eSIM to a compatible smartwatch so it can stay connected when your phone is not nearby. This service is only for compatible Apple Watch and Samsung Galaxy Watch cellular models.
        </p>
      </section>

      <section className="watch-esim-summary">
        <div>
          <FaSimCard />
          <span>Monthly price</span>
          <strong>$10 inc. GST</strong>
        </div>
        <div>
          <FaMobileAlt />
          <span>Included data</span>
          <strong>1GB/month</strong>
        </div>
      </section>

      <section className="watch-esim-section">
        <div className="watch-esim-section-heading">
          <h2>Before You Start</h2>
          <p>Check these requirements before trying to activate the watch eSIM.</p>
        </div>
        <div className="watch-esim-requirements">
          <div>
            <h3>Compatible watches only</h3>
            <p>Apple Watch Series 4 or later, including Ultra, and supported Samsung Galaxy Watch cellular models. GPS-only watches are not compatible.</p>
          </div>
          <div>
            <h3>Same brand ecosystem</h3>
            <p>Use Apple Watch with iPhone, and Samsung Galaxy Watch with a compatible Samsung phone. Other watch ecosystems are not supported.</p>
          </div>
          <div>
            <h3>Eligible mobile plan</h3>
            <p>The companion plan requires an eligible 5G mobile service with voice capability. Data-only services are not eligible.</p>
          </div>
        </div>
      </section>

      <nav className="watch-esim-quick-links" aria-label="Watch eSIM page shortcuts">
        <a href="#remove-esim">Remove eSIM from your watch</a>
        <a href="#watch-esim-faq">View FAQs</a>
      </nav>

      <section className="watch-esim-section" id="setup">
        <div className="watch-esim-section-heading">
          <h2>Choose Your Watch</h2>
          <p>Select your watch type to see the activation steps.</p>
        </div>
        <div className="watch-esim-device-picker">
          <button
            type="button"
            className={selectedDevice === "apple" ? "active" : ""}
            onClick={() => setSelectedDevice("apple")}
          >
            <FaApple />
            <span>Apple Watch</span>
            <small>Use the Apple Watch app on iPhone</small>
          </button>
          <button
            type="button"
            className={selectedDevice === "samsung" ? "active" : ""}
            onClick={() => setSelectedDevice("samsung")}
          >
            <FaMobileAlt />
            <span>Samsung Galaxy Watch</span>
            <small>Use the Galaxy Wearable app</small>
          </button>
        </div>

        <div className="watch-esim-setup-panel">
          <div className="watch-esim-section-heading">
            <span>{selectedDevice === "apple" ? <FaApple /> : <FaMobileAlt />} {selectedDevice === "apple" ? "Apple Watch" : "Samsung Galaxy Watch"}</span>
            <h2>{selectedDevice === "apple" ? "Activate an Apple Watch companion eSIM" : "Activate a Samsung watch companion eSIM"}</h2>
            <p>{selectedDevice === "apple" ? "Follow these steps from the Apple Watch app on your iPhone." : "Follow these steps from the Galaxy Wearable app on your Samsung phone."}</p>
          </div>
          <div className="watch-esim-step-cards">
            {activeSteps.map((step, index) => (
              <article className="watch-esim-step-card" key={step.title}>
                <div className="watch-esim-step-copy">
                  <span>Step {index + 1}</span>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
                <figure className="watch-esim-step-image">
                  <img src={step.image} alt={step.imageAlt} loading="lazy" />
                </figure>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="watch-esim-section compact" id="remove-esim">
        <div className="watch-esim-section-heading">
          <h2>Remove the eSIM from your watch</h2>
          <p>Use this only when you want to remove the profile from the device. Contact Jortel separately to cancel the paid service.</p>
        </div>
        <div className="watch-esim-warning">
          <div>
            <FaExclamationTriangle />
          </div>
          <div>
            <h2>Removing the eSIM is not the same as cancelling the service</h2>
            <p>
              If you remove the Telstra plan/eSIM from your watch, it only removes the plan from that device. It does not unsubscribe you from the monthly companion plan.
            </p>
            <p>
              To stop being billed, contact Jortel and request cancellation of the eSIM Companion Plan.
            </p>
          </div>
        </div>
        <div className="watch-esim-step-cards">
          {removalSteps.map((step, index) => (
            <article className="watch-esim-step-card" key={step.title}>
              <div className="watch-esim-step-copy">
                <span>Removal Step {index + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
              <figure className="watch-esim-step-image">
                <img src={step.image} alt={step.imageAlt} loading="lazy" />
              </figure>
            </article>
          ))}
        </div>
      </section>

      <section className="watch-esim-faq" id="watch-esim-faq">
        <div className="watch-esim-section-heading">
          <h2>Watch eSIM FAQs</h2>
          <p>Common questions before activation and cancellation.</p>
        </div>
        <div className="watch-esim-faq-grid">
          {faqs.map((faq) => (
            <details key={faq.question}>
              <summary>{faq.question}</summary>
              <p>{faq.answer}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="watch-esim-cta">
        <h2>Ready to add a watch eSIM?</h2>
        <p>Contact Jortel to confirm your mobile plan and device are eligible before activation.</p>
        <Link to="/contact">Contact Jortel</Link>
      </section>
    </main>
  );
}
